import {createContext, useCallback, useContext, useEffect, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import { API_BASE_URL, API_KEY } from "../config/config";
import axios from "axios";
import { AuthContext } from "./authContext";
import {smoothScrollToElement} from "../utils/utils";

const ThreadContext = createContext(null);

const ThreadProvider = ({ children }) => {
	const { visitor } = useContext(AuthContext);

	const location = useLocation();

	const [threadLoading, setThreadLoading] = useState(false);
	const [thread, setThread] = useState(null);

	const [threadData, setThreadData] = useState({
		temp_off: false,
		bumpMessage: ''
	});

	const [firstPost, setFirstPost] = useState(null);

	const { threadTitleId } = useParams();

	let threadId = null;

	if (threadTitleId) {
		const urlPattern = /^([\w-]+)\.(\d+)$/;
		const match = threadTitleId.match(urlPattern);
		if (match) {
			const [_, threadTitle, matchThreadId] = match;
			if (matchThreadId) {
				threadId = Number(matchThreadId);
			}
		}
	}

	const fetchThread = useCallback(async (isInitialLoad = false) => {
		if (!visitor) return;

		if (thread !== null && isInitialLoad) return; // Already loaded

		if (threadLoading) return; // Loading

		try {
			setThreadLoading(true);

			const config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}?with_first_post=true`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const fetchedThread = response.data.thread;
			setThread(fetchedThread);

			setThreadData(prevData => ({
				...prevData,
				temp_off: fetchedThread.ft_temp_off,
			}));

			const fetchedFirstPost = response.data.first_post;
			setFirstPost(fetchedFirstPost);

			setThreadLoading(false);
		} catch (error) {
			console.error('Failed to fetch thread:', error);
			setThreadLoading(false);
		}
	}, [thread, threadId, threadLoading, visitor]);

	const toggleTempOff = async () => {
		try {
			const config = {
				method: 'post',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/temp-off`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const status = response.data.status;

			setThreadData(prevData => ({
				...prevData,
				temp_off: (status !== 'working'),
			}));
		} catch (error) {
			console.error('Failed to toggle temp off:', error);
		}
	};

	const bump = async () => {
		try {
			const config = {
				method: 'post',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/bump`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials: true,
			};

			const response = await axios.request(config);
			const message = response.data.message;

			setThreadData(prevData => ({
				...prevData,
				bumpMessage: message,
			}));
		} catch (error) {
			console.error('Failed to bump:', error);
			setThreadData(prevData => ({
				...prevData,
				bumpMessage: error,
			}));
		}
	};

	const [postsInitialLoaded, setPostsInitialLoaded] = useState(false);
	const [posts, setPosts] = useState([]);
	const [postsLoading, setPostsLoading] = useState(false);
	const [postsPage, setPostsPage] = useState(1);
	const [hasMorePosts, setHasMorePosts] = useState(false);
	const [pagination, setPagination] = useState({
		current_page: 1,
		last_page: 1,
		per_page: 20,
		shown: 0,
		total: 0
	});

	const fetchPosts = useCallback(async (isInitial = false, page = 1) => {
		if (!visitor) return;

		if (postsLoading) return; // Loading

		if (!Number.isInteger(page)) return; // Not a valid page

		if (page === pagination.current_page && !isInitial) return; // Already loaded

		if (isInitial && postsInitialLoaded) return; // Already loaded

		try {
			setPostsLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/posts?page=${page}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			const response = await axios.request(config);
			let newPosts = response.data.posts;
			if (typeof newPosts === "object") {
				newPosts = Object.values(newPosts);
			}

			setPosts(newPosts);

			const pagination = response.data.pagination;
			setPagination(pagination);

			setHasMorePosts(pagination.current_page < pagination.last_page);
			setPostsLoading(false);
			setPostsInitialLoaded(true);
		} catch (error) {
			console.error('Failed to fetch posts:', error);
			setPostsLoading(false);
			setPostsInitialLoaded(true);
		}
	}, [visitor, postsLoading, pagination.current_page, postsInitialLoaded, threadId]);

	const handlePageChange = (data) => {
		if (visitor && threadId) {
			const selectedPage = data.selected + 1;
			if (selectedPage !== postsPage && Number.isInteger(selectedPage)) {
				window.location.replace(`${thread.view_url}#page-${selectedPage}`);
				window.location.reload();
			}
		}
	}

	const [userReadDataLoading, setUserReadDataLoading] = useState(false);
	const [userReadData, setUserReadData] = useState(null);

	const fetchUserReadData = async () => {
		if (!visitor) return;

		if (userReadData !== null) return; // Already loaded

		if (userReadDataLoading) return; // Loading

		try {
			setUserReadDataLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/user-read-data`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			const response = await axios.request(config);
			setUserReadData(response.data.results);
			setUserReadDataLoading(false);
		} catch (error) {
			console.error('Failed to fetch user read data:', error);
			setUserReadData([]);
			setUserReadDataLoading(false);
		}
	}

	const [replyTemplatesLoading, setReplyTemplatesLoading] = useState(false);
	const [replyTemplates, setReplyTemplates] = useState(null);

	const fetchReplyTemplates = async () => {
		if (!visitor) return;

		if (replyTemplates !== null) return; // Already loaded

		if (replyTemplatesLoading) return; // Loading

		try {
			setReplyTemplatesLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/reply-templates`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			const response = await axios.request(config);
			let templates = response.data.templates;
			if (typeof templates === "object") {
				templates = Object.values(templates);
			}

			setReplyTemplates(templates);
			setReplyTemplatesLoading(false);
		} catch (error) {
			console.error('Failed to fetch reply templates:', error);
			setReplyTemplates([]);
			setReplyTemplatesLoading(false);
		}
	}

	const [newReplies, setNewReplies] = useState([]);

	const resetNewReplies = () => {
		setNewReplies([]);
	}

	const loadMoreNewReplies = (post) => {
		setNewReplies([...newReplies, post]);
	}

	const [videosLoading, setVideosLoading] = useState(false);
	const [videos, setVideos] = useState(null);

	const fetchVideos = async () => {
		if (!visitor) return;

		if (videos !== null) return; // Already loaded

		if (videosLoading) return; // Loading

		try {
			setVideosLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/videos`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			const response = await axios.request(config);
			let videos = response.data.videos;
			if (typeof videos === "object") {
				videos = Object.values(videos);
			}

			setVideos(videos);
			setVideosLoading(false);
		} catch (error) {
			console.error('Failed to fetch videos:', error);

			setVideos([]);
			setVideosLoading(false);
		}
	}

	const [ratingsInitialLoaded, setRatingsInitialLoaded] = useState(false);
	const [ratings, setRatings] = useState([]);
	const [ratingsLoading, setRatingsLoading] = useState(false);
	const [ratingsPage, setRatingsPage] = useState(1);
	const [hasMoreRatings, setHasMoreRatings] = useState(true);

	const fetchRatings = useCallback(async (isInitial = false) => {
		if (!visitor) return;

		if (ratingsLoading) return; // Loading

		if (isInitial && ratingsInitialLoaded) return; // Already loaded

		try {
			setRatingsLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${threadId}/ratings?page=${ratingsPage}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			const response = await axios.request(config);
			let newRatings = response.data.ratings;
			if (typeof newRatings === "object") {
				newRatings = Object.values(newRatings);
			}

			setRatings([...ratings, ...newRatings]);

			let pagination = response.data.pagination;

			setHasMoreRatings(pagination.current_page < pagination.last_page);
			setRatingsPage(ratingsPage + 1);
			setRatingsLoading(false);
			setRatingsInitialLoaded(true);
		} catch (error) {
			console.error('Failed to fetch ratings:', error);
			setRatingsLoading(false);
			setRatingsInitialLoaded(true);
		}
	}, [visitor, ratingsLoading, ratingsInitialLoaded, threadId, ratingsPage, ratings]);

	const loadMoreRatings = () => {
		if (!hasMoreRatings) {
			return;
		}

		setRatingsPage(ratingsPage + 1);
		fetchRatings(false);
	}

	useEffect(() => {
		if (visitor && threadId) {
			fetchThread(true);

			const hash = location.hash;
			const pageMatch = hash.match(/#page-(\d+)/);
			const page = pageMatch ? parseInt(pageMatch[1], 10) : 1;

			setPostsPage(page);
			fetchPosts(true, page).then(r => {
				if (posts.length > 0) {
					const queryParams = new URLSearchParams(location.search);
					const postId = queryParams.get("post_id");

					if (postId) {
						smoothScrollToElement(`post-${postId}`);
					}
				}
			});
		}
	}, [visitor, threadId, fetchPosts, location.hash, fetchThread, location.search, posts.length]);

	return (
		<ThreadContext.Provider value={{
			fetchThread, thread, firstPost,
			posts, postsLoading, hasMorePosts, fetchPosts, pagination, handlePageChange,
			threadData,
			toggleTempOff, bump,
			userReadData, fetchUserReadData,
			replyTemplates, fetchReplyTemplates,
			newReplies, loadMoreNewReplies, resetNewReplies,
			videos, fetchVideos,
			ratings, ratingsLoading, hasMoreRatings, loadMoreRatings, fetchRatings,
		}}>
			{children}
		</ThreadContext.Provider>
	);
}

export { ThreadContext, ThreadProvider };